import { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from '@apollo/client'

import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Input from 'shared/ui/Input'
import Select from 'shared/ui/Select'
import IslandModal from 'shared/components/modals/IslandModal'
import { RECOVER_PROJECT_META } from 'data/layoutcreator/mutations/migrations'

const CUSTOMER_PROJECTS_ROOT = 'Layoutcreator\\Customer Projects'

const coverTypeOptions = ['soft', 'hard']

interface RecoverProjectMetaModalProps {
  isOpen: boolean
  onClose: () => void
}

const RecoverProjectMetaModal: React.FC<RecoverProjectMetaModalProps> = ({
  isOpen,
  onClose
}) => {
  const [projectName, setProjectName] = useState('')
  const [templatePath, setTemplatePath] = useState('')
  const [chilifolderName, setChilifolderName] = useState('')
  const [groupId, setGroupId] = useState('')
  const [coverTypeIndex, setCoverTypeIndex] = useState(0)

  const [recoverProjectMeta] = useMutation(RECOVER_PROJECT_META)

  const chiliPath =
    groupId && chilifolderName
      ? `${CUSTOMER_PROJECTS_ROOT}\\${groupId}\\${chilifolderName}`
      : ''

  const isFormValid = projectName && templatePath && chilifolderName && groupId

  const handleSubmit = async () => {
    const toastId = toast.loading('Recovering project meta...')

    try {
      const { data, errors } = await recoverProjectMeta({
        variables: {
          projectName,
          templatePath,
          chilifolderName,
          groupId,
          coverType: coverTypeOptions[coverTypeIndex]
        }
      })

      toast.dismiss(toastId)

      if (errors) {
        toast.error('Failed to recover project meta')
        return
      }

      if (data?.recoverProjectMeta) {
        toast.success('Project meta recovered successfully')
        setProjectName('')
        setTemplatePath('')
        setChilifolderName('')
        setGroupId('')
        setCoverTypeIndex(0)
        onClose()
      } else {
        toast.error('Recovery returned false — check server logs')
      }
    } catch (error: any) {
      toast.dismiss(toastId)
      toast.error(error.message || 'Failed to recover project meta')
    }
  }

  return (
    <IslandModal
      isOpen={isOpen}
      closeModal={onClose}
      width={600}
      title="Recover Project Meta"
    >
      <Flex direction="column">
        <Flex direction="column" rowGap={16}>
          <Input
            value={projectName}
            onChange={setProjectName}
            label="Project Name"
            placeholder="Display name for the recovered project"
          />

          <Input
            value={templatePath}
            onChange={setTemplatePath}
            label="Template Path"
            placeholder="e.g. Layoutcreator\Master Templates\..."
          />

          <Input
            value={groupId}
            onChange={setGroupId}
            label="Group ID"
            placeholder="Enter group ID"
          />

          <Input
            value={chilifolderName}
            onChange={setChilifolderName}
            label="Chili Folder Name"
            placeholder="Name of the existing folder in Chili"
          />

          {chiliPath ? (
            <Flex direction="column">
              <label style={{ fontSize: '12px', color: '#888', marginBottom: 4 }}>
                Chili path (auto-generated)
              </label>
              <span style={{ fontSize: '13px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {chiliPath}
              </span>
            </Flex>
          ) : null}

          <Flex direction="column">
            <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: 8 }}>
              Cover Type
            </label>
            <Select
              options={coverTypeOptions}
              activeIndex={coverTypeIndex}
              onChange={(_, index) => setCoverTypeIndex(index)}
            />
          </Flex>
        </Flex>

        <Flex marginTop={46}>
          <Button
            intent="primary-brand-01"
            textId="Recover Project"
            size="lg"
            isDisabled={!isFormValid}
            onPress={handleSubmit}
          />
        </Flex>
      </Flex>
    </IslandModal>
  )
}

export default RecoverProjectMetaModal
